import { useState, useRef, useEffect } from 'react';
import { adminApi } from './AdminContext';
import { 
  ArrowLeft, Camera, Upload, User, Building2, Phone, Mail,
  Trash2, Check, Loader, FileText, Image as ImageIcon,
  Users, Truck, Handshake, X, ChevronDown
} from 'lucide-react';

// Labor types for laborer creation
const LABOR_TYPES = [
  'Aluminium Technician', 'Glass Installer', 'Painter', 
  'Drywall Worker', 'Cleaner', 'Helper', 'Other'
];

// Supplier types
const SUPPLIER_TYPES = [
  'Aluminium', 'Glass', 'Hardware', 'Paint', 
  'Flooring', 'Tools', 'General Materials', 'Other'
];

// Partnership types
const PARTNERSHIP_TYPES = [
  'General Contractor', 'Subcontractor', 'Aluminium Specialist',
  'Glass Specialist', 'Electrical Contractor', 'Plumbing Contractor',
  'HVAC Contractor', 'Painting Contractor', 'Flooring Contractor',
  'Cleaning Services', 'Other'
];

const CardScanner = ({ onBack }) => {
  const [scanning, setScanning] = useState(false);
  const [scannedContacts, setScannedContacts] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [saveCategory, setSaveCategory] = useState('');
  const [categoryType, setCategoryType] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const data = await adminApi.getScannedContacts();
      setScannedContacts(data);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async (file) => {
    if (!file) return;

    setScanning(true);
    setResult(null);

    try {
      const data = await adminApi.scanBusinessCard(file);
      if (data.success) {
        setResult(data.contact);
        loadContacts();
      } else {
        alert('Failed to scan card. Please try again.');
      }
    } catch (error) {
      console.error('Scan error:', error);
      alert('Failed to scan card. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this contact?')) {
      await adminApi.deleteScannedContact(id);
      loadContacts();
      if (result?.id === id) setResult(null);
    }
  };

  const openSaveModal = (contact) => {
    setSelectedContact(contact);
    setSaveCategory('');
    setCategoryType('');
    setShowSaveModal(true);
  };

  const closeSaveModal = () => {
    setShowSaveModal(false);
    setSelectedContact(null);
    setSaveCategory('');
    setCategoryType('');
  };

  const handleSaveToCategory = async () => {
    if (!saveCategory || !categoryType) {
      alert('Please select a category and type');
      return;
    }

    setSaving(true);
    try {
      const contact = selectedContact;
      
      if (saveCategory === 'laborer') {
        await adminApi.createLaborer({
          name: contact.name || 'Unknown',
          phone: contact.phone || '',
          labor_type: categoryType,
          daily_rate: 0,
          experience_level: 'Junior',
          availability: 'Available',
          notes: `Imported from scanned business card${contact.company ? ` - ${contact.company}` : ''}${contact.email ? ` - ${contact.email}` : ''}`,
          tags: ['scanned']
        });
        alert('Laborer created successfully!');
      } else if (saveCategory === 'supplier') {
        await adminApi.createSupplier({
          company_name: contact.company || contact.name || 'Unknown',
          contact_person: contact.name || '',
          phone: contact.phone || '',
          email: contact.email || '',
          supplier_type: categoryType,
          location: 'Dubai',
          price_level: 'Medium',
          notes: 'Imported from scanned business card',
          tags: ['scanned']
        });
        alert('Supplier created successfully!');
      } else if (saveCategory === 'partner') {
        await adminApi.createPartner({
          company_name: contact.company || contact.name || 'Unknown',
          contact_person: contact.name || '',
          phone: contact.phone || '',
          email: contact.email || '',
          website: '',
          partnership_type: categoryType,
          description: '',
          notes: 'Imported from scanned business card'
        });
        alert('Partner created successfully!');
      }
      
      closeSaveModal();
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getTypeOptions = () => {
    if (saveCategory === 'laborer') return LABOR_TYPES;
    if (saveCategory === 'supplier') return SUPPLIER_TYPES;
    if (saveCategory === 'partner') return PARTNERSHIP_TYPES;
    return [];
  };

  const ContactCard = ({ contact, showActions = true }) => (
    <div className="bg-[#0A0F1C] border border-white/10 rounded-lg p-4">
      <div className="space-y-3">
        {contact.name && (
          <div className="flex items-center gap-3">
            <User size={18} className="text-blue-400" />
            <span className="text-white">{contact.name}</span>
          </div>
        )}
        {contact.company && (
          <div className="flex items-center gap-3">
            <Building2 size={18} className="text-purple-400" />
            <span className="text-white">{contact.company}</span>
          </div>
        )}
        {contact.phone && (
          <a 
            href={`https://wa.me/${contact.phone.replace(/[^0-9]/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 text-[#25D366]"
          >
            <Phone size={18} />
            <span>{contact.phone}</span>
          </a>
        )}
        {contact.email && (
          <a 
            href={`mailto:${contact.email}`}
            className="flex items-center gap-3 text-blue-400"
          >
            <Mail size={18} />
            <span>{contact.email}</span>
          </a>
        )}
      </div>

      {showActions && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
          <button
            onClick={() => openSaveModal(contact)}
            className="flex-1 py-2 bg-[#25D366] text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
            data-testid="save-contact-btn"
          >
            <Check size={16} />
            Save Contact
          </button>
          <button
            onClick={() => handleDelete(contact.id)}
            className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg"
            data-testid="delete-contact-btn"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#060B14]">
      <header className="bg-[#0A0F1C] border-b border-white/10 sticky top-0 z-40">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={onBack} className="text-white p-2" data-testid="back-btn">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-white">Business Card Scanner</h1>
        </div>
      </header>

      <main className="p-4 pb-24">
        {/* Scan Section */}
        <div className="bg-[#0A0F1C] border border-white/10 rounded-lg p-6 mb-6">
          <h2 className="text-white font-semibold mb-4 text-center">Scan a Business Card</h2>
          <p className="text-slate-400 text-sm text-center mb-6">
            Take a photo or upload an image to extract contact information automatically
          </p>

          {scanning ? (
            <div className="flex flex-col items-center py-8">
              <div className="animate-spin w-12 h-12 border-3 border-[#25D366] border-t-transparent rounded-full mb-4"></div>
              <p className="text-slate-400">Scanning and extracting text...</p>
              <p className="text-slate-500 text-sm">This may take a few seconds</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {/* Camera Capture */}
              <label className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#25D366] transition-colors">
                <div className="w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center">
                  <Camera size={28} className="text-white" />
                </div>
                <span className="text-white font-medium">Take Photo</span>
                <span className="text-slate-500 text-xs">Use camera</span>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleScan(e.target.files[0])}
                  data-testid="camera-input"
                />
              </label>

              {/* File Upload */}
              <label className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#25D366] transition-colors">
                <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center">
                  <Upload size={28} className="text-white" />
                </div>
                <span className="text-white font-medium">Upload</span>
                <span className="text-slate-500 text-xs">From gallery</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleScan(e.target.files[0])}
                  data-testid="file-input"
                />
              </label>
            </div>
          )}
        </div>

        {/* Latest Scan Result */}
        {result && (
          <div className="mb-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Check size={18} className="text-[#25D366]" />
              Extracted Information
            </h3>
            <ContactCard contact={result} />
          </div>
        )}

        {/* Previous Scans */}
        <div>
          <h3 className="text-white font-semibold mb-3">Previous Scans</h3>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-[#25D366] border-t-transparent rounded-full"></div>
            </div>
          ) : scannedContacts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No scanned cards yet</p>
              <p className="text-sm text-slate-500">Scan a business card to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scannedContacts.map((contact) => (
                <ContactCard key={contact.id} contact={contact} />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center">
          <div className="bg-[#0A0F1C] w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">Save Contact As</h2>
              <button 
                onClick={closeSaveModal}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <X size={20} className="text-white" />
              </button>
            </div>

            {/* Contact Preview */}
            <div className="p-4 border-b border-white/10 bg-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#25D366]/20 rounded-full flex items-center justify-center">
                  <User size={20} className="text-[#25D366]" />
                </div>
                <div>
                  <p className="text-white font-medium">{selectedContact?.name || 'Unknown'}</p>
                  <p className="text-slate-400 text-sm">{selectedContact?.phone || selectedContact?.email || ''}</p>
                </div>
              </div>
            </div>

            {/* Category Selection */}
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Select Category *</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => { setSaveCategory('laborer'); setCategoryType(''); }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                      saveCategory === 'laborer' 
                        ? 'border-blue-500 bg-blue-500/20' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid="category-laborer"
                  >
                    <Users size={24} className={saveCategory === 'laborer' ? 'text-blue-400' : 'text-slate-400'} />
                    <span className={`text-sm font-medium ${saveCategory === 'laborer' ? 'text-blue-400' : 'text-slate-400'}`}>
                      Laborer
                    </span>
                  </button>
                  <button
                    onClick={() => { setSaveCategory('supplier'); setCategoryType(''); }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                      saveCategory === 'supplier' 
                        ? 'border-purple-500 bg-purple-500/20' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid="category-supplier"
                  >
                    <Truck size={24} className={saveCategory === 'supplier' ? 'text-purple-400' : 'text-slate-400'} />
                    <span className={`text-sm font-medium ${saveCategory === 'supplier' ? 'text-purple-400' : 'text-slate-400'}`}>
                      Supplier
                    </span>
                  </button>
                  <button
                    onClick={() => { setSaveCategory('partner'); setCategoryType(''); }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                      saveCategory === 'partner' 
                        ? 'border-cyan-500 bg-cyan-500/20' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid="category-partner"
                  >
                    <Handshake size={24} className={saveCategory === 'partner' ? 'text-cyan-400' : 'text-slate-400'} />
                    <span className={`text-sm font-medium ${saveCategory === 'partner' ? 'text-cyan-400' : 'text-slate-400'}`}>
                      Partner
                    </span>
                  </button>
                </div>
              </div>

              {/* Type Selection */}
              {saveCategory && (
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Select {saveCategory === 'laborer' ? 'Labor' : saveCategory === 'supplier' ? 'Supplier' : 'Partnership'} Type *
                  </label>
                  <select
                    value={categoryType}
                    onChange={(e) => setCategoryType(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
                    data-testid="type-select"
                  >
                    <option value="">Select type...</option>
                    {getTypeOptions().map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Save Button */}
              <button
                onClick={handleSaveToCategory}
                disabled={!saveCategory || !categoryType || saving}
                className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors ${
                  !saveCategory || !categoryType || saving
                    ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    : 'bg-[#25D366] text-white hover:bg-[#20BD5A]'
                }`}
                data-testid="confirm-save-btn"
              >
                {saving ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Save as {saveCategory ? saveCategory.charAt(0).toUpperCase() + saveCategory.slice(1) : '...'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardScanner;
